import React, { useState } from "react";
import { useTranslation } from "./useTranslation";

const Support = () => {
  const { t } = useTranslation();
  const [searchTerm, setSearchTerm] = useState("");
  const [openFaq, setOpenFaq] = useState(null);

  // Keep IDs stable; text comes from i18n
  const faqs = [
    { id: 1, qKey: "support.faqs.1.q", aKey: "support.faqs.1.a", catKey: "support.categories.overview" },
    { id: 2, qKey: "support.faqs.2.q", aKey: "support.faqs.2.a", catKey: "support.categories.compatibility" },
    { id: 3, qKey: "support.faqs.3.q", aKey: "support.faqs.3.a", catKey: "support.categories.installation" },
    { id: 4, qKey: "support.faqs.4.q", aKey: "support.faqs.4.a", catKey: "support.categories.dualSim" },
    { id: 5, qKey: "support.faqs.5.q", aKey: "support.faqs.5.a", catKey: "support.categories.dataUsage" },
    { id: 6, qKey: "support.faqs.6.q", aKey: "support.faqs.6.a", catKey: "support.categories.planMgmt" },
    { id: 7, qKey: "support.faqs.7.q", aKey: "support.faqs.7.a", catKey: "support.categories.deviceReqs" },
    { id: 8, qKey: "support.faqs.8.q", aKey: "support.faqs.8.a", catKey: "support.categories.troubleshooting" },
    { id: 9, qKey: "support.faqs.9.q", aKey: "support.faqs.9.a", catKey: "support.categories.refunds" },
    { id: 10, qKey: "support.faqs.10.q", aKey: "support.faqs.10.a", catKey: "support.categories.supportHours" },
  ];

  const localizedFaqs = faqs.map(f => ({
    ...f,
    question: t(f.qKey),
    answer: t(f.aKey),
    category: t(f.catKey),
  }));

  const filteredFaqs = localizedFaqs.filter(
    (faq) =>
      faq.question.toLowerCase().includes(searchTerm.toLowerCase()) ||
      faq.answer.toLowerCase().includes(searchTerm.toLowerCase()) ||
      faq.category.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const toggleFaq = (id) => setOpenFaq(openFaq === id ? null : id);

  const supportChannels = [
    {
      title: t("support.channels.email.title"),
      description: t("support.channels.email.description"),
      contact: t("support.channels.email.contact"),
      action: t("support.channels.email.action"),
      href: "mailto:support@routedlabs.com",
    },
    {
      title: t("support.channels.chat.title"),
      description: t("support.channels.chat.description"),
      contact: t("support.channels.chat.contact"),
      action: t("support.channels.chat.action"),
      href: "https://wa.me/14155947485", // WhatsApp link
    },
  ];

  return (
    <main className="min-h-screen bg-white text-gray-900">
      <article className="max-w-5xl mx-auto px-6 lg:px-8 py-20">
        {/* Title */}
        <header className="text-center mb-16">
          <h1 className="text-4xl font-serif font-bold tracking-tight mb-4">
            {t("support.title")}
          </h1>
        </header>

        {/* Contact and Channels */}
        <section className="mb-12">
          <h2 className="text-2xl font-serif font-semibold mb-4">
            {t("support.contactInfo")}
          </h2>
          <div className="grid md:grid-cols-2 gap-6">
            {supportChannels.map((channel, index) => (
              <div
                key={index}
                className="border border-gray-200 rounded-md p-6"
              >
                <h3 className="text-lg font-medium mb-2">{channel.title}</h3>
                <p className="text-sm leading-relaxed text-gray-800 mb-2">
                  {channel.description}
                </p>
                <p className="text-sm font-medium text-gray-900 mb-4">
                  {channel.contact}
                </p>
                <a
                  href={channel.href}
                  className="inline-block px-4 py-2 border border-gray-300 rounded-md text-sm font-medium hover:bg-gray-50"
                >
                  {channel.action}
                </a>
              </div>
            ))}
          </div>
        </section>

        {/* FAQs */}
        <section className="mb-16">
          <h2 className="text-2xl font-serif font-semibold mb-6">
            {t("support.faqTitle")}
          </h2>
          <div className="divide-y divide-gray-200 border border-gray-200 rounded-md">
            {filteredFaqs.length === 0 && (
              <div className="p-6 text-sm text-gray-700">
                {t("support.noResults")}
              </div>
            )}
            {filteredFaqs.map((faq) => (
              <div key={faq.id} className="p-6">
                <button
                  onClick={() => toggleFaq(faq.id)}
                  className="w-full text-left"
                >
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-900">
                        {faq.question}
                      </p>
                      <p className="text-xs text-gray-500 mt-1">
                        {t("support.categoryLabel")} {faq.category}
                      </p>
                    </div>
                    <span className="text-xs text-gray-500 ml-4">
                      {openFaq === faq.id ? t("support.hide") : t("support.show")}
                    </span>
                  </div>
                </button>
                {openFaq === faq.id && (
                  <div className="mt-4 text-sm leading-relaxed text-gray-800">
                    {faq.answer}
                  </div>
                )}
              </div>
            ))}
          </div>
        </section>

        {/* Formal Notices */}
        <section className="pt-10 border-t border-gray-200">
          <h2 className="text-2xl font-serif font-semibold mb-4">
            {t("support.noticesTitle")}
          </h2>
          <p className="text-sm leading-relaxed text-gray-800 mb-2">
            {t("support.noticesLine1.before")}{" "}
            <a href="mailto:support@routedlabs.com" className="underline">
              support@routedlabs.com
            </a>
            {t("support.noticesLine1.after")}
          </p>
          <p className="text-sm leading-relaxed text-gray-800">
            {t("support.noticesLine2")}
          </p>
        </section>
      </article>
    </main>
  );
};

export default Support;
